import { useState } from 'react';
import {
  frameworkEnable,
  frameworks as frameworksRoute,
  guardianKillSwitch,
  policyAutonomy,
  policyAutonomyTest,
  policyAutonomyUpdate,
  policyBreakers,
  policyPosture,
  policyPresets,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { ErrorNote } from '../../ErrorNote';
import { AboutThis } from '../../ui/AboutThis';
import { OWNER_ONLY, useIsOwner } from '../../shell/aiControls';
import { notServed, pre, sx, useApi, type PolicyRole } from './util';

const MONO = 'font-variant-numeric:tabular-nums;';
const SECTION = 'break-inside:avoid;margin-bottom:6px;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:10px 2px 8px;';
const HEAD = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const MUTED = 'font-size:16px;color:#54606C;';

function Head({ dot, children }: { dot: string; children: string }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
      <span style={sx('width:7px;height:7px;border-radius:50%;background:' + dot + ';')} />
      <span style={sx(HEAD)}>{children}</span>
    </div>
  );
}

/** Not served yet: the section says so rather than drawing made-up rows. */
function Waiting({ what }: { what: string }) {
  return <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:8px;')}>{what} is not served by the back end yet, so nothing is shown here.</div>;
}

/** Autonomy (design pcIsGlobal, T12765): autonomy levels, the global pause, presets, breakers, frameworks. */
export function AutonomyTab({ pr }: { pr: PolicyRole }) {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const isOp = pr === 'operator';
  const canWrite = isOp && roReason === null;
  const writeTitle = roReason ?? (isOp ? undefined : 'Autonomy is set by Aithora for every building.');

  const autonomy = useApi((c, signal) => policyAutonomy(c, { signal }), []);
  const breakers = useApi((c, signal) => policyBreakers(c, { signal }), []);
  const presets = useApi((c, signal) => policyPresets(c, { signal }), []);
  const posture = useApi((c, signal) => policyPosture(c, { query: pr === 'facility' ? {} : { scope: 'network' }, signal }), [pr]);
  const fws = useApi((c, signal) => frameworksRoute(c, { signal }), []);

  const [levels, setLevelsRaw] = useState<Record<string, number>>({});
  // The last dry run (POST /policy/autonomy/test) of the unsaved levels, cleared by any change.
  const [tested, setTested] = useState<{ note: string } | { error: true } | null>(null);
  const setLevels: typeof setLevelsRaw = (v) => { setTested(null); setLevelsRaw(v); };
  const [saveOpen, setSaveOpen] = useState(false);
  const [kill, setKill] = useState<'engage' | 'release' | null>(null);
  const [reason, setReason] = useState('');
  const [enableFw, setEnableFw] = useState<{ id: string; name: string } | null>(null);
  const [enabled, setEnabled] = useState<Record<string, number>>({});

  const rows = autonomy.data?.rows ?? [];
  const tiers = autonomy.data?.tiers ?? ['L1', 'L2', 'L3', 'L4', 'L5'];
  const dirtyKeys = rows.filter((r) => levels[r.key] !== undefined && levels[r.key] !== r.level).map((r) => r.key);
  const dirty = dirtyKeys.length;
  const halted = (posture.data?.halts ?? []).some((h) => h.item === 'guardian_pause:global' || h.kind === 'kill_switch');
  const killed = !!posture.data && halted;
  // Only the owner (by email, from the server) turns the AI back on: anyone else sees RESUME
  // held, with the reason, instead of a 403 after confirming.
  const isOwner = useIsOwner();
  const resumeHeld = killed && isOwner === false;

  return (
    <div data-testid="policy-autonomy">
      <div style={sx('font-size:16px;margin-bottom:16px;')}>
        <b style={sx('font-weight:700;')}>System-wide posture.</b> <span style={sx('color:#54606C;')}>Autonomy levels, response playbooks and circuit breakers that span every layer.</span>
      </div>
      <div style={sx('column-width:420px;column-gap:28px;')}>
        <div style={sx(SECTION)}>
          <Head dot="#2F5C97">Autonomy matrix</Head>
          {/* V9: the explainer opens on the "i" (design k14174). */}
          <AboutThis inline textStyle="font-size:16px;color:#54606C;margin-top:7px;">
            How far each system type may act on its own before a human is required. L1 observe {'·'} L3 act, notify after {'·'} L5 full autonomy.
          </AboutThis>
          {autonomy.error && notServed(autonomy.error) && <Waiting what="The autonomy matrix" />}
          {autonomy.error && !notServed(autonomy.error) && <ErrorNote error={autonomy.error} onRetry={autonomy.reload} compact />}
          {autonomy.data && (
            <>
              <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) repeat(5,1fr);gap:7px;margin-top:8px;')}>
                <span />
                {tiers.map((t) => (
                  <span key={t} style={sx('font-size:14px;color:#54606C;text-align:center;')}>{t}</span>
                ))}
              </div>
              {rows.map((r) => {
                const cur = levels[r.key] ?? r.level;
                return (
                  <div key={r.key} role="radiogroup" aria-label={r.name} style={sx('display:grid;grid-template-columns:minmax(0,1fr) repeat(5,1fr);gap:7px;align-items:center;margin-top:5px;')}>
                    <span style={sx('min-width:0;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;')}>{r.name}</span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>{r.note}</span>
                    </span>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <a
                        key={i}
                        href="#"
                        role="radio"
                        aria-checked={i === cur}
                        aria-disabled={!canWrite || undefined}
                        title={writeTitle ?? (i === cur ? r.name + ' is at L' + i + ' now' : 'Set ' + r.name + ' to L' + i + ' (saved with Save)')}
                        onClick={(e) => { pre(e); if (canWrite && i !== cur) setLevels((p) => ({ ...p, [r.key]: i })); }}
                        style={sx('display:flex;align-items:center;justify-content:center;height:30px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;' + (i === cur ? 'background:#2F5C97;color:#fff;cursor:default;' : 'background:rgba(34,39,46,.05);color:#54606C;') + (!canWrite ? 'cursor:not-allowed;' : ''))}
                      >
                        {'L' + i}
                      </a>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={sx(SECTION + 'display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:10px;')}>
          <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#B3261E;')} />
            <span style={sx(HEAD)}>Global kill switch</span>
          </span>
          <span data-testid="kill-chip" style={sx(MONO + 'font-size:14px;font-weight:700;letter-spacing:.07em;border-radius:5px;padding:3px 8px;flex:0 0 auto;' + (killed ? 'color:#B3261E;background:rgba(179,38,30,.13);' : 'color:#1F6B52;background:rgba(47,138,111,.13);'))}>
            {posture.data ? (killed ? 'ENGAGED' : 'ARMED') : '–'}
          </span>
          <span style={sx('flex:1;min-width:220px;font-size:16px;color:#54606C;')}>
            {killed ? 'Engaged. All AI generation is frozen; safety escalation and 911 stay live.' : 'Armed and standing by. One control halts all actions gate-wide.'}
          </span>
          {pr !== 'readonly' && <a
            href="#"
            data-testid="kill-toggle"
            aria-disabled={!canWrite || resumeHeld || undefined}
            title={writeTitle ?? (resumeHeld ? OWNER_ONLY : killed ? 'Only the owner turns the AI back on. The server checks.' : undefined)}
            onClick={(e) => { pre(e); if (canWrite && !resumeHeld) { setReason(''); setKill(killed ? 'release' : 'engage'); } }}
            style={sx('display:inline-flex;align-items:center;gap:8px;height:36px;padding:0 18px;border-radius:8px;white-space:nowrap;' + MONO + 'font-size:14px;font-weight:700;letter-spacing:.09em;text-decoration:none;flex:0 0 auto;' + (killed ? 'background:#FCFAF4;color:#B3261E;border:1px solid rgba(179,38,30,.42);' : 'background:#B3261E;color:#fff;') + (resumeHeld ? 'opacity:.5;cursor:not-allowed;' : ''))}
          >
            {killed ? 'RESUME' : 'KILL'}
          </a>}
          {resumeHeld && <span data-testid="resume-owner-only" style={sx('flex-basis:100%;font-size:16px;color:#54606C;')}>{OWNER_ONLY}</span>}
        </div>

        <div style={sx(SECTION)}>
          <Head dot="#2F5C97">Response playbooks</Head>
          <div style={sx(MUTED + 'margin-top:7px;margin-bottom:14px;')}>Pre-armed escalation sequences. When a condition fires, the gate runs the playbook automatically.</div>
          {presets.error && notServed(presets.error) && <Waiting what="The preset list" />}
          {presets.error && !notServed(presets.error) && <ErrorNote error={presets.error} onRetry={presets.reload} compact />}
          {presets.data && (
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0 18px;margin-top:6px;')}>
              {presets.data.presets.map((p) => (
                <div key={p.key} style={sx('border-top:1px solid rgba(34,39,46,.08);padding:7px 0;display:flex;align-items:flex-start;gap:12px;')}>
                  <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:700;')}>{p.name}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;')}>{p.sequence}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;flex:0 1 auto;min-width:0;')}>preset</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={sx(SECTION)}>
          <Head dot="#2F5C97">Circuit breakers</Head>
          <div style={sx(MUTED + 'margin-top:7px;margin-bottom:14px;')}>System-wide tiers that escalate as more guards trip at once, the failsafe above individual guards.</div>
          {breakers.error && notServed(breakers.error) && <Waiting what="The circuit-breaker tiers" />}
          {breakers.error && !notServed(breakers.error) && <ErrorNote error={breakers.error} onRetry={breakers.reload} compact />}
          {breakers.data && (
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px;')}>
              {breakers.data.breakers.map((b) => {
                const c = b.tone === 'stop' ? '#B3261E' : b.tone === 'watch' ? '#3C454F' : '#2F5C97';
                return (
                  <div key={b.key} style={sx('padding:6px 0 6px 10px;border-left:3px solid ' + c + ';')}>
                    <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:' + c + ';')}>{b.name}</div>
                    <div style={sx('font-size:14px;color:#54606C;margin-top:6px;')}>{b.rule}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={sx(SECTION)}>
          <Head dot="#1F6B52">Compliance frameworks</Head>
          <div style={sx(MUTED + 'margin-top:7px;margin-bottom:14px;')}>Enable a framework to seal its bundled policies onto the gate in one step.</div>
          {fws.error && <ErrorNote error={fws.error} onRetry={fws.reload} compact />}
          {fws.data && (
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0 18px;margin-top:6px;')}>
              {fws.data.frameworks.map((f) => {
                const on = enabled[f.id] !== undefined;
                const meta = f.region.toUpperCase() + ' · ' + f.policyCount + (f.policyCount === 1 ? ' policy' : ' policies');
                return (
                  <div key={f.id} style={sx('padding:6px 0;display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;border-top:1px solid rgba(34,39,46,.08);' + (on ? 'box-shadow:inset 3px 0 0 #1F6B52;padding-left:9px;' : ''))}>
                    <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;')}>{f.name}</span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;')}>{on ? meta + ' · sealed' : meta}</span>
                    </span>
                    {(pr !== 'readonly' || on) && <a
                      href="#"
                      aria-disabled={!canWrite || on || undefined}
                      title={writeTitle}
                      onClick={(e) => { pre(e); if (canWrite && !on) setEnableFw({ id: f.id, name: f.name }); }}
                      style={sx('display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (on ? 'background:rgba(47,138,111,.13);color:#1F6B52;border:1px solid rgba(47,138,111,.34);' : 'background:#2F5C97;color:#fff;'))}
                    >
                      {on ? 'Enabled' : 'Enable'}
                    </a>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {pr !== 'readonly' && (
      <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;margin-top:18px;padding-top:14px;border-top:1px solid rgba(34,39,46,.12);')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (dirty ? '#3C454F' : '#54606C') + ';')} />
        <span data-testid="autonomy-dirty" style={sx('font-size:16px;' + (dirty ? 'color:#3C454F;font-weight:600;' : 'color:#54606C;'))}>{tested && 'note' in tested ? tested.note : tested ? 'The test run failed. Try again.' : dirty === 0 ? 'No unsaved changes' : dirty === 1 ? '1 unsaved change' : dirty + ' unsaved changes'}</span>
        <span style={sx('flex:1;')} />
        <a
          href="#"
          aria-disabled={!dirty || undefined}
          title="A dry run of the unsaved levels: who would have to approve them. Nothing is saved."
          onClick={(e) => {
            pre(e);
            if (!dirty) return;
            const body = Object.fromEntries(dirtyKeys.map((k) => [k, levels[k]]));
            policyAutonomyTest(client, { body: { levels: body } })
              .then((r) => setTested({ note: r.note }))
              .catch(() => setTested({ error: true }));
          }}
          style={sx('display:inline-flex;align-items:center;height:26px;padding:0 15px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;')}>
          Test
        </a>
        <a href="#" data-testid="autonomy-reset" aria-disabled={!dirty || undefined} title={dirty ? 'Puts every level back to what is saved' : 'No unsaved changes to reset'} onClick={(e) => { pre(e); if (dirty) setLevels({}); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 15px;border-top:1px solid rgba(34,39,46,.14);font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (dirty ? 'color:#3C454F;' : 'color:rgba(84,96,108,.6);cursor:default;'))}>
          Reset
        </a>
        <a href="#" aria-disabled={!dirty || !canWrite || undefined} title={writeTitle} onClick={(e) => { pre(e); if (dirty && canWrite) setSaveOpen(true); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 18px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (dirty ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'))}>
          Save
        </a>
      </div>
      )}

      {saveOpen && (
        <ConfirmDialog
          title="Save the autonomy levels?"
          confirmLabel="Save"
          body={
            <ul>
              {dirtyKeys.map((k) => {
                const r = rows.find((x) => x.key === k)!;
                return (
                  <li key={k}>
                    <b>{r.name}</b> L{r.level} {'→'} L{levels[k]}
                  </li>
                );
              })}
            </ul>
          }
          onConfirm={async () => {
            const next: Record<string, number> = {};
            dirtyKeys.forEach((k) => (next[k] = levels[k]));
            await policyAutonomyUpdate(client, { body: withConfirm({ levels: next }) });
            setLevels({});
            autonomy.reload();
          }}
          onClose={() => setSaveOpen(false)}
        />
      )}
      {kill && (
        <ConfirmDialog
          title={kill === 'engage' ? 'Halt the AI in every building?' : 'Turn the AI back on everywhere?'}
          confirmLabel={kill === 'engage' ? 'Halt everything' : 'Resume'}
          danger={kill === 'engage'}
          body={
            <>
              <p>
                {kill === 'engage'
                  ? 'Every AI reply and action stops in every building at once. Safety escalation and 911 stay live. Only the owner can turn it back on.'
                  : 'Only the owner can turn the AI back on. The server checks.'}
              </p>
              {kill === 'engage' && (
                <label className="field">
                  <span className="field__label">Reason</span>
                  <textarea className="field__input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                </label>
              )}
            </>
          }
          onConfirm={async () => {
            await guardianKillSwitch(client, { body: withConfirm({ action: kill, scope: 'global', ...(reason.trim() ? { reason: reason.trim() } : {}) }) });
            posture.reload();
          }}
          onClose={() => setKill(null)}
        />
      )}
      {enableFw && (
        <ConfirmDialog
          title={`Enable ${enableFw.name}?`}
          confirmLabel="Enable"
          body={<p>Its bundled policies are added to the gate for your tenant and the change is written to the Truth Ledger.</p>}
          onConfirm={async () => {
            const r = await frameworkEnable(client, { params: { id: enableFw.id }, body: withConfirm({}) });
            setEnabled((p) => ({ ...p, [enableFw.id]: r.policiesCreated }));
          }}
          onClose={() => setEnableFw(null)}
        />
      )}
    </div>
  );
}
